import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import { createFileLogger } from "../../utils/logger";
import useEntityStore from "../../stores/entityStore";
import useSpaceStore from "../../stores/spaceStore";
import { useCommandRegistryStore } from "../../stores/commandRegistryStore";
import { resolveEntityId } from "./entityIdResolver";

const log = createFileLogger('ENTITY_DELETE');

export const entityDeleteCommand: SelfContainedCommand = {
  id: 'entity_delete',
  description: `Delete an entity by its ID. This is a soft delete - the entity is marked as trashed but not permanently removed. Automatically closes any windows displaying the entity.

**SUPPORTED ENTITIES:**
- All entity types (Documents, Notes, Sheets, Canvas, Presentations, Folders, Files)

**Use Cases:**
- Remove unwanted entities
- Clean up old documents
- Delete empty folders
- Archive completed work`,
  autonomyLevel: 'ask',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'entityId',
        type: 'string',
        description: 'The ID of the entity to delete',
        required: true
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { entityId: 'doc_123' },
        expectedOutput: createSuccessResult('Entity deleted successfully', {
          entityId: 'doc_123',
          closedWindows: 1,
          message: 'Entity deleted successfully'
        }),
        description: 'Delete an entity',
        context: 'When removing unwanted content'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 300,
    complexity: 'medium'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    let { entityId } = params;
    
    try {
      // ✅ Resolve llmId to _id if needed
      const resolution = resolveEntityId(entityId);
      
      if (resolution.isLlmId && !resolution.entity) {
        return createErrorResult(
          'Entity not found',
          `No entity found with llmId: ${entityId}`,
          'validation',
          { data: { providedId: entityId } }
        );
      }
      
      entityId = resolution.resolved;
      
      log.info('[entity_delete] Deleting entity:', { 
        originalId: params.entityId,
        resolvedId: entityId,
        wasLlmId: resolution.isLlmId
      });
      
      const spaceStore = useSpaceStore.getState();
      const windowsToClose = Object.entries(spaceStore.windows)
        .filter(([_, window]) => window.entityId === entityId)
        .map(([windowId, _]) => windowId);
      
      log.info('[entity_delete] Found windows to close:', { 
        entityId, 
        windowCount: windowsToClose.length,
        windowIds: windowsToClose
      });
      
      const { executeCommand } = useCommandRegistryStore.getState();
      for (const windowId of windowsToClose) {
        try {
          await executeCommand('space_close_window', { windowId });
          log.info('[entity_delete] Closed window:', { windowId, entityId });
        } catch (error) {
          log.warn('[entity_delete] Failed to close window:', { windowId, error });
        }
      }
      
      const entityStore = useEntityStore.getState();
      
      // Get entity name before deleting
      const entity = entityStore.entities.find(e => e._id === entityId);
      const entityName = entity?.name || 'Entity';
      
      await entityStore.actions.softDeleteEntity(entityId);
      
      log.info('[entity_delete] ✅ Entity deleted:', { 
        entityId,
        entityName,
        closedWindows: windowsToClose.length
      });
      
      return createSuccessResult(
        `Deleted "${entityName}"`,
        {
          entityId,
          entityName,
          closedWindows: windowsToClose.length,
          message: `Deleted "${entityName}"`
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'entityStore.actions',
            affectedEntities: [entityId],
            closedWindows: windowsToClose
          } as any,
          ui: {
            refreshContext: true
          }
        }
      );
      
    } catch (error: any) {
      log.error('[entity_delete] Error:', error);
      return createErrorResult(
        'Failed to delete entity',
        `Failed to delete entity: ${error.message || 'Unknown error'}`,
        'execution',
        {
          data: { entityId },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

